// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CardHeader } from '@/components/card-header';
import { TruncatedText } from '@/components/truncated-text';
import { InfoOutlined, Launch } from '@/constants/hardfin-standard-icons';
import {
  ShippingCarrier,
  getShippingCarrierLabel,
  getTrackingURLForShippingCarrier,
} from '@/constants/shipping-carrier';
import { Shipment } from '@/shipment/api/types';
import { getShipmentTracking } from '@/tracking/api/get-shipment-tracking';
import { TrackingData } from '@/tracking/api/types';
import { TrackingHistory } from '@/tracking/components/tracking-history';
import {
  Box,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';

interface TrackingContentProps {
  shipment: Pick<Shipment, 'key' | 'trackingNumberDetails'>;
}

const TRACKING_NUMBER_MAX_WIDTH = 240;

// Caps the card so a long delivery-event history scrolls instead of growing
// the right gutter, matching the attachments list cap in
// entity-attachments-section.tsx
const TRACKING_CARD_MAX_HEIGHT_PX = 360;

/**
 * Displays basic carrier link when tracking data is not yet available.
 * The carrier label and external-link icon stay at their intrinsic widths;
 * the tracking number expands up to a ceiling and truncates with an ellipsis
 * when the available width forces it to shrink.
 */
function CarrierLink(props: {
  carrier: ShippingCarrier;
  referenceNumber: string;
}) {
  const trackingUrl = getTrackingURLForShippingCarrier(
    props.carrier,
    props.referenceNumber
  );

  return (
    <Link
      href={trackingUrl ?? undefined}
      target="_blank"
      sx={{
        display: 'block',
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      <Stack alignItems="center" direction="row" gap={1} minWidth={0}>
        <Typography variant="body2" sx={{ flexShrink: 0 }}>
          {getShippingCarrierLabel(props.carrier)}
        </Typography>
        <Box flex={1} minWidth={0} maxWidth={TRACKING_NUMBER_MAX_WIDTH}>
          <TruncatedText text={props.referenceNumber} variant="body2" />
        </Box>
        <Launch fontSize="small" sx={{ flexShrink: 0 }} />
      </Stack>
    </Link>
  );
}

const ETA_FORMAT = 'MMM d, yyyy h:mm a (ZZZZ)';

/**
 * Formats an ETA in the user's local timezone for display.
 */
function formatEta(dateTime: DateTime): string {
  return dateTime.setZone('local').toFormat(ETA_FORMAT);
}

/**
 * Displays the ETA. When the carrier has revised it from the original ETA,
 * a small info icon appears next to the value; hovering it surfaces the
 * original ETA in a tooltip.
 */
function ETAValue(props: { eta: DateTime; originalEta: DateTime | null }) {
  const isRevised =
    props.originalEta !== null && !props.eta.equals(props.originalEta);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
      <Typography variant="subtitle2">{formatEta(props.eta)}</Typography>
      {isRevised && props.originalEta !== null && (
        <Tooltip arrow title={`Original ETA: ${formatEta(props.originalEta)}`}>
          <InfoOutlined
            fontSize="inherit"
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * Renders a single label/value row inside the summary's two-column grid.
 * The label column is fixed-width and right-aligned so values line up across
 * rows even when label lengths differ.
 */
function SummaryRow(props: { label: string; children: React.ReactNode }) {
  return (
    <>
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="right"
        sx={{ alignSelf: 'baseline' }}
      >
        {props.label}
      </Typography>
      <Box sx={{ alignSelf: 'baseline' }}>{props.children}</Box>
    </>
  );
}

/**
 * Displays the tracking-level summary (service level + ETA) above the
 * per-event history. These values are reported alongside the event list by
 * the tracking provider and describe the shipment as a whole.
 */
function TrackingSummary(props: { data: TrackingData }) {
  if (!props.data.serviceLevelName && !props.data.eta) {
    return null;
  }

  return (
    <Box
      marginBottom={1.5}
      display="grid"
      gridTemplateColumns="56px 1fr"
      columnGap={1}
      rowGap={0.5}
    >
      {props.data.serviceLevelName && (
        <SummaryRow label="Service">
          <Typography variant="subtitle2">
            {props.data.serviceLevelName}
          </Typography>
        </SummaryRow>
      )}
      {props.data.eta && (
        <SummaryRow label="ETA">
          <ETAValue eta={props.data.eta} originalEta={props.data.originalEta} />
        </SummaryRow>
      )}
    </Box>
  );
}

/**
 * Fetches and renders the scrollable tracking content (summary + timeline)
 * as a single flowing block. Intended to be placed inside an ancestor that
 * provides the scroll context.
 */
function ShipmentTrackingContent(props: TrackingContentProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['shipment', props.shipment.key, 'tracking'],
    queryFn: () => getShipmentTracking(props.shipment.key),
    enabled: Boolean(
      props.shipment.trackingNumberDetails?.carrier &&
        props.shipment.trackingNumberDetails.trackingNumber
    ),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error || !data?.data) {
    return (
      <Typography variant="body2" color="text.secondary">
        Unable to load tracking data
      </Typography>
    );
  }

  return (
    <>
      <TrackingSummary data={data.data} />
      <TrackingHistory data={data.data} compact hideHeader embedded />
    </>
  );
}

/**
 * Displays the carrier tracking information for a shipment.
 * Heading + carrier link stay pinned at the top; the summary and timeline
 * scroll together as one unit once the card reaches its height cap.
 */
export function ShipmentCarrierTracking(props: TrackingContentProps) {
  if (
    !props.shipment.trackingNumberDetails?.carrier ||
    !props.shipment.trackingNumberDetails.trackingNumber
  ) {
    return (
      <Paper variant="outlined">
        <CardHeader
          title="Tracking"
          sx={{
            '& .MuiTypography-root': {
              lineHeight: '2.5rem',
            },
            paddingX: 2,
            paddingY: 1,
          }}
        />
        <Stack padding={2} paddingTop={0}>
          <Typography variant="body2" color="text.secondary">
            Not set
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        padding: 0,
        maxHeight: TRACKING_CARD_MAX_HEIGHT_PX,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title="Tracking"
        sx={{
          '& .MuiTypography-root': {
            lineHeight: '2.5rem',
          },
          flexShrink: 0,
          paddingX: 2,
          paddingY: 1,
        }}
      >
        <Box minWidth={0}>
          <CarrierLink
            carrier={props.shipment.trackingNumberDetails.carrier}
            referenceNumber={
              props.shipment.trackingNumberDetails.trackingNumber
            }
          />
        </Box>
      </CardHeader>
      <Stack flex={1} minHeight={0} overflow="auto" padding={2} paddingTop={0}>
        <ShipmentTrackingContent shipment={props.shipment} />
      </Stack>
    </Paper>
  );
}
